import { DestroyRef, Directive, Input, Optional, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, ControlValueAccessor, FormControl, FormControlDirective } from '@angular/forms';

@Directive()
export abstract class BaseFormDirective implements ControlValueAccessor {
  @ViewChild(FormControlDirective, { static: true })
  formControlDirective: FormControlDirective;

  @Input()
  formControl: FormControl;

  @Input()
  formControlName: string;

  private readonly destroyRef = inject(DestroyRef);

  // Used only when the consumer binds a bare [(ngModel)] with no formControl/formControlName,
  // since the internal template always needs a real FormControl to bind to.
  private readonly standaloneControl = new FormControl('');
  private onStandaloneTouched: () => void = () => {};

  get control() {
    return (
      (this.formControl as FormControl) ||
      (this.controlContainer?.control.get(this.formControlName) as FormControl) ||
      this.standaloneControl
    );
  }

  protected get hasExternalControl(): boolean {
    return !!(this.formControl || (this.controlContainer && this.formControlName));
  }

  constructor(@Optional() protected controlContainer: ControlContainer) {}

  registerOnTouched(fn: any): void {
    if (this.hasExternalControl) {
      this.formControlDirective?.valueAccessor.registerOnTouched(fn);
    } else {
      this.onStandaloneTouched = fn;
    }
  }

  registerOnChange(fn: any): void {
    if (this.hasExternalControl) {
      this.formControlDirective?.valueAccessor.registerOnChange(fn);
    } else {
      this.standaloneControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fn);
    }
  }

  writeValue(obj: any): void {
    if (this.hasExternalControl) {
      this.formControlDirective?.valueAccessor.writeValue(obj);
    } else {
      this.standaloneControl.setValue(obj);
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.hasExternalControl) {
      this.formControlDirective?.valueAccessor.setDisabledState(isDisabled);
    } else if (isDisabled) {
      this.standaloneControl.disable();
    } else {
      this.standaloneControl.enable();
    }
  }

  protected notifyStandaloneTouched(): void {
    if (!this.hasExternalControl) {
      this.onStandaloneTouched();
    }
  }
}
